import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  Flex,
  Image,
  Button,
  VStack,
  HStack,
  Badge,
  useColorModeValue,
  Divider,
  Select,
  Skeleton,
  Grid,
  GridItem,
  IconButton,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Tooltip,
  Spinner
} from "@chakra-ui/react";
import { 
  FaShoppingCart, 
  FaShare, 
  FaHeart, 
  FaRegHeart, 
  FaTag, 
  FaFlask, 
  FaSeedling, 
  FaBug, 
  FaInfoCircle 
} from "react-icons/fa";
import { API_BASE_URL } from './config/api_keys';

export default function ChemicalsPage() {
  const [chemicals, setChemicals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const iconBgColor = useColorModeValue('green.50', 'green.900');

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE_URL}/api/chemicals`);
        const data = await res.json();
        setChemicals(data);
      } catch (error) {
        console.error('Error fetching chemicals:', error);
        toast({
          title: "Error",
          description: "Failed to load chemicals",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [toast]);

  const handleCardClick = (chemical) => {
    setSelected(chemical);
    setSelectedVariant(null); // Don't auto-select any variant
    setCurrentImageIndex(0);
    onOpen();
  };

  const handleBuyNow = (chemical) => {
    toast({
      title: "Success",
      description: `Added ${chemical.name} to cart!`,
      status: "success",
      duration: 3000,
      isClosable: true,
    });
    // Here you can implement cart functionality
  };

  const handleShare = async (chemical) => {
    const shareData = {
      title: chemical.name,
      text: `Check out this ${chemical.type}: ${chemical.name} - ₹${selectedVariant ? selectedVariant.sellingPrice : chemical.sellingPrice}`,
      url: window.location.href
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        // Fallback for browsers that don't support Web Share API
        await navigator.clipboard.writeText(`${shareData.title} - ${shareData.text}`);
        toast({
          title: "Success",
          description: "Link copied to clipboard!",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error sharing:', error);
      toast({
        title: "Error",
        description: "Failed to share",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleFavorite = (chemicalId) => {
    setFavorites(prev => 
      prev.includes(chemicalId) 
        ? prev.filter(id => id !== chemicalId)
        : [...prev, chemicalId]
    );
    toast({
      title: "Success",
      description: favorites.includes(chemicalId) ? 'Removed from favorites' : 'Added to favorites',
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };

  const getTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'fertilizer':
        return <FaSeedling color="green" />;
      case 'pesticide':
        return <FaBug color="red" />;
      case 'herbicide':
        return <FaFlask color="orange" />;
      default:
        return <FaFlask color="blue" />;
    }
  };

  const getTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'fertilizer':
        return 'green';
      case 'pesticide':
        return 'red';
      case 'herbicide':
        return 'orange';
      default:
        return 'blue';
    }
  };

  // Add function to calculate savings
  const calculateSavings = (mrp, sellingPrice) => {
    if (mrp && sellingPrice && mrp > sellingPrice) {
      return mrp - sellingPrice;
    }
    return 0;
  };

  const nextImage = () => {
    if (selected && selected.images && selected.images.length > 0) {
      setCurrentImageIndex((prev) => 
        prev === selected.images.length - 1 ? 0 : prev + 1
      );
    }
  };

  const prevImage = () => {
    if (selected && selected.images && selected.images.length > 0) {
      setCurrentImageIndex((prev) => 
        prev === 0 ? selected.images.length - 1 : prev - 1
      );
    }
  };

  if (loading) {
    return (
      <Container maxW="container.xl" py={6}>
        <Box textAlign="center" mb={6}>
          <FaFlask size={60} color="green" style={{ marginBottom: '16px' }} />
          <Heading size="lg" color="green.600" mb={4}>
            Chemicals & Fertilizers
          </Heading>
        </Box>
        <SimpleGrid columns={{ base: 2, md: 3, lg: 4 }} spacing={4}>
          {[...Array(8)].map((_, index) => (
            <Box key={index} borderWidth="1px" borderRadius="lg" overflow="hidden">
              <Skeleton height="200px" />
              <Box p={4}>
                <Skeleton height="24px" mb={2} />
                <Skeleton height="20px" mb={2} />
                <Skeleton height="20px" />
              </Box>
            </Box>
          ))}
        </SimpleGrid>
      </Container>
    );
  }

  return (
    <Box 
      minH="100vh" 
      py={6} 
      bg="linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)"
    >
      <Container maxW="container.xl">
        {/* Header */}
        <Box textAlign="center" mb={8}>
          <FaFlask size={60} color="green" style={{ marginBottom: '16px' }} />
          <Heading size="2xl" color="green.600" mb={4} fontWeight="bold">
            Chemicals & Fertilizers
          </Heading>
          <Text fontSize="lg" color="gray.600">
            High-quality agricultural inputs for better crop yields
          </Text>
        </Box>

        {chemicals.length === 0 && (
          <Box textAlign="center" py={10}>
            <FaFlask size={80} color="gray" style={{ marginBottom: '16px' }} />
            <Heading size="lg" color="gray.500">
              No chemicals or fertilizers found.
            </Heading>
          </Box>
        )}

        {/* Chemicals Grid */}
        <SimpleGrid columns={{ base: 2, md: 3, lg: 4 }} spacing={4}>
          {chemicals.map((chem) => (
            <Box 
              key={chem._id}
              borderWidth="1px" 
              borderRadius="lg" 
              overflow="hidden"
              bg={bgColor}
              transition="all 0.3s ease-in-out"
              _hover={{
                transform: 'translateY(-8px)',
                boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
              }}
              position="relative"
              cursor="pointer"
              onClick={() => handleCardClick(chem)}
              height="280px"
              display="flex"
              flexDirection="column"
            >
              <Image
                src={chem.images && chem.images.length > 0 ? chem.images[0] : (chem.imageUrl || "/placeholder-chemical.png")}
                alt={chem.name}
                height="120px"
                width="100%"
                objectFit="cover"
                flexShrink={0}
              />
              
              <Box p={3} flex={1} display="flex" flexDirection="column" justifyContent="space-between">
                {/* Type Icon and Name */}
                <Flex align="center" gap={2} mb={2}>
                  {getTypeIcon(chem.type)}
                  <Heading size="sm" fontWeight="bold" noOfLines={1} fontSize="12px">
                    {chem.name}
                  </Heading>
                </Flex>

                {/* Type Badge */}
                <Badge colorScheme={getTypeColor(chem.type)} mb={2} size="sm">
                  {chem.type}
                </Badge>

                {/* Price */}
                <Flex align="center" gap={2} mb={2} flexWrap="wrap" justify="center">
                  {chem.discount && (
                    <Text fontWeight="bold" color="green.500" fontSize="10px" bg="green.50" px={2} py={1} borderRadius="md">
                      {chem.discount}% Off
                    </Text>
                  )}
                  {chem.mrp && (
                    <Text as="span" color="gray.500" textDecoration="line-through" fontSize="10px">
                      ₹{chem.mrp}
                    </Text>
                  )}
                  {chem.sellingPrice && (
                    <Text as="span" fontWeight="bold" color="black" fontSize="12px">
                      ₹{chem.sellingPrice}
                    </Text>
                  )}
                </Flex>

                {/* Category */}
                <Text fontSize="xs" color="gray.600" mb={2} textAlign="center">
                  {chem.category?.name || chem.category}
                </Text>

                {/* Description Preview */}
                <Text fontSize="xs" color="gray.600" noOfLines={2} textAlign="center">
                  {chem.description?.substring(0, 60) || 'High-quality agricultural input for better crop yields...'}
                  {chem.description?.length > 60 && '...'}
                </Text>
              </Box>
            </Box>
          ))}
        </SimpleGrid>

        {/* Details Modal */}
        <Modal isOpen={isOpen} onClose={onClose} size="xl">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>
              <Flex align="center" gap={3}>
                {getTypeIcon(selected?.type)}
                <Heading size="md">{selected?.name}</Heading>
              </Flex>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              {selected && (
                <Box>
                  {/* Image Gallery */}
                  {(selected.images && selected.images.length > 0) || selected.imageUrl ? (
                    <Box position="relative" mb={6} height="250px" bg="gray.50" borderRadius="md" display="flex" alignItems="center" justifyContent="center">
                      <Button
                        position="absolute"
                        left={2}
                        top="50%"
                        transform="translateY(-50%)"
                        onClick={prevImage}
                        size="sm"
                        colorScheme="blackAlpha"
                      >
                        ‹
                      </Button>
                      <Image
                        src={selected.images && selected.images.length > 0 ? selected.images[currentImageIndex] : selected.imageUrl}
                        alt={`${selected.name} - Image ${currentImageIndex + 1}`}
                        maxH="100%"
                        maxW="100%"
                        objectFit="contain"
                      />
                      <Button
                        position="absolute"
                        right={2}
                        top="50%"
                        transform="translateY(-50%)"
                        onClick={nextImage}
                        size="sm"
                        colorScheme="blackAlpha"
                      >
                        ›
                      </Button>
                      {(selected.images && selected.images.length > 1) && (
                        <Flex position="absolute" bottom={2} left="50%" transform="translateX(-50%)" gap={1}>
                          {selected.images.map((_, index) => (
                            <Box
                              key={index}
                              w={2}
                              h={2}
                              borderRadius="full"
                              bg={index === currentImageIndex ? "white" : "whiteAlpha.500"}
                              cursor="pointer"
                              onClick={() => setCurrentImageIndex(index)}
                            />
                          ))}
                        </Flex>
                      )}
                    </Box>
                  ) : null}

                  {/* Price Section */}
                  <Box mb={4} p={4} bg="gray.50" borderRadius="md">
                    <Flex align="center" gap={2} flexWrap="wrap">
                      {selectedVariant ? (
                        <>
                          {selectedVariant.discount && (
                            <Badge colorScheme="green" fontSize="sm">
                              {selectedVariant.discount}% Off
                            </Badge>
                          )}
                          {selectedVariant.mrp && (
                            <Text textDecoration="line-through" color="gray.500" fontSize="sm">
                              ₹{selectedVariant.mrp}
                            </Text>
                          )}
                          {selectedVariant.sellingPrice && (
                            <Text fontWeight="bold" fontSize="lg">
                              ₹{selectedVariant.sellingPrice}
                            </Text>
                          )}
                        </>
                      ) : (
                        <>
                          {selected.discount && (
                            <Badge colorScheme="green" fontSize="sm">
                              {selected.discount}% Off
                            </Badge>
                          )}
                          {selected.mrp && (
                            <Text textDecoration="line-through" color="gray.500" fontSize="sm">
                              ₹{selected.mrp}
                            </Text>
                          )}
                          {selected.sellingPrice && (
                            <Text fontWeight="bold" fontSize="lg">
                              ₹{selected.sellingPrice}
                            </Text>
                          )}
                        </>
                      )}
                    </Flex>
                  </Box>

                  {/* Variants Selection */}
                  {selected.variants && selected.variants.length > 0 && (
                    <Box mb={4}>
                      <Heading size="sm" mb={3}>Select Pack Size</Heading>
                      <SimpleGrid columns={{ base: 2, md: 3 }} spacing={2}>
                        {selected.variants.map((variant, index) => {
                          const savings = calculateSavings(variant.mrp, variant.sellingPrice);
                          return (
                            <Button
                              key={index}
                              variant={selectedVariant === variant ? "solid" : "outline"}
                              colorScheme={selectedVariant === variant ? "green" : "gray"}
                              onClick={() => setSelectedVariant(variant)}
                              size="sm"
                              flexDirection="column"
                              height="auto"
                              py={2}
                            >
                              <Text fontSize="xs" fontWeight="bold">{variant.name}</Text>
                              <Text fontSize="sm" fontWeight="bold" color="green.500">₹{variant.sellingPrice}</Text>
                              {savings > 0 && (
                                <Text fontSize="xs" color="green.600">Save ₹{savings}</Text>
                              )}
                            </Button>
                          );
                        })}
                      </SimpleGrid>
                    </Box>
                  )}

                  {/* Selected Variant Details */}
                  {selectedVariant && (
                    <Box mb={4} p={4} bg="green.50" borderRadius="md">
                      <Heading size="sm" mb={3}>Selected: {selectedVariant.name}</Heading>
                      <SimpleGrid columns={2} spacing={2}>
                        <Box>
                          <Text fontSize="xs" color="gray.600">MRP:</Text>
                          <Text fontSize="sm" textDecoration="line-through" color="gray.500">₹{selectedVariant.mrp}</Text>
                        </Box>
                        <Box>
                          <Text fontSize="xs" color="gray.600">Selling Price:</Text>
                          <Text fontSize="sm" fontWeight="bold">₹{selectedVariant.sellingPrice}</Text>
                        </Box>
                        <Box>
                          <Text fontSize="xs" color="gray.600">Discount:</Text>
                          <Text fontSize="sm" color="green.500">{selectedVariant.discount}%</Text>
                        </Box>
                        <Box>
                          <Text fontSize="xs" color="gray.600">You Save:</Text>
                          <Text fontSize="sm" color="green.600" fontWeight="bold">₹{calculateSavings(selectedVariant.mrp, selectedVariant.sellingPrice)}</Text>
                        </Box>
                      </SimpleGrid>
                    </Box>
                  )}

                  {/* Product Details in Row/Column Format */}
                  <Box mb={4}>
                    <Heading size="md" color="green.600" mb={4}>
                      Product Details
                    </Heading>
                    <SimpleGrid columns={2} spacing={4}>
                      <Box>
                        <Text fontSize="sm" color="gray.600" fontWeight="500">Type:</Text>
                        <Text fontSize="sm" color="gray.800">{selected.type}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="sm" color="gray.600" fontWeight="500">Category:</Text>
                        <Text fontSize="sm" color="gray.800">{selected.category?.name || selected.category}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="sm" color="gray.600" fontWeight="500">Usage Type:</Text>
                        <Text fontSize="sm" color="gray.800">{selected.usageType || 'N/A'}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="sm" color="gray.600" fontWeight="500">Target:</Text>
                        <Text fontSize="sm" color="gray.800">{selected.targetDiseaseOrCrop || 'N/A'}</Text>
                      </Box>
                    </SimpleGrid>
                  </Box>

                  {/* Description */}
                  <Box mb={4}>
                    <Heading size="md" color="green.600" mb={4}>
                      Description
                    </Heading>
                    <Text color="gray.600" fontSize="sm" lineHeight="1.6">
                      {selected.description || 'No description provided.'}
                    </Text>
                  </Box>

                  {/* Pricing & Rating */}
                  <Box mb={4}>
                    <Heading size="md" color="green.600" mb={4}>
                      Pricing & Rating
                    </Heading>
                    <SimpleGrid columns={2} spacing={4}>
                      <Box>
                        <Text fontSize="sm" color="gray.600" fontWeight="500">Price:</Text>
                        <Text fontSize="lg" fontWeight="bold" color="green.600">
                          ₹{selectedVariant ? selectedVariant.sellingPrice : selected.sellingPrice}
                        </Text>
                      </Box>
                      <Box>
                        <Text fontSize="sm" color="gray.600" fontWeight="500">Rating:</Text>
                        <Flex align="center" gap={2}>
                          <HStack spacing={1}>
                            {[...Array(5)].map((_, i) => (
                              <Box key={i} color={i < 4 ? "yellow.400" : "gray.300"}>
                                ★
                              </Box>
                            ))}
                          </HStack>
                          <Text fontSize="sm" color="gray.600">
                            (4.5 out of 5 stars)
                          </Text>
                        </Flex>
                      </Box>
                      <Box>
                        <Text fontSize="sm" color="gray.600" fontWeight="500">Availability:</Text>
                        <Text fontSize="sm" color="green.600" fontWeight="500">In Stock</Text>
                      </Box>
                    </SimpleGrid>
                  </Box>
                </Box>
              )}
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onClose}>
                Close
              </Button>
              <Button 
                colorScheme="green"
                leftIcon={<FaShoppingCart />}
                onClick={() => {
                  handleBuyNow(selected);
                  onClose();
                }}
              >
                Add to Cart
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Container>
    </Box>
  );
} 